import { useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import { merge } from 'lodash';
import parse from 'parse-json';
import { ChartOptions } from './RiskIndicator';

if (typeof Highcharts === 'object') {
    HighchartsMore(Highcharts);
}

interface RiskGaugeChartProps {
    value?: number;
    options?: ChartOptions[];
    showDashedOuterLine?: boolean;
    dashedLineColor?: string;
    indicatorColor?: string;
    backgroundColor?: string;
    dialColor?: string;
}

const RiskGaugeChart = (props: RiskGaugeChartProps) => {
    const { value, options, showDashedOuterLine, ...rest } = props;
    const chartRef = useRef<any>(null);

    function parseJSONWithFallback(jsonString = '') {
        try {
            return JSON.parse(jsonString);
        } catch (error) {
            console.error('Error parsing JSON:', error);
            return null; // Or provide a suitable fallback value
        }
    }

    // get hex from color objects
    const dashedLineColor =
        typeof rest?.dashedLineColor === 'string'
            ? parseJSONWithFallback(rest?.dashedLineColor)?.hex
            : '';

    const indicatorColor =
        typeof rest?.indicatorColor === 'string'
            ? parseJSONWithFallback(rest?.indicatorColor)?.hex
            : '';

    const backgroundColor =
        typeof rest?.backgroundColor === 'string'
            ? parseJSONWithFallback(rest?.backgroundColor)?.hex
            : '';

    const dialColor =
        typeof rest?.dialColor === 'string' ? parseJSONWithFallback(rest?.dialColor)?.hex : '';

    // chart options
    let chartOptions = {
        chart: {
            type: 'gauge',
            plotBackgroundColor: null,
            plotBackgroundImage: null,
            plotBorderWidth: 0,
            plotShadow: false,
            width: 100,
            height: 100,
            events: false,
        },
        credits: {
            enabled: false,
        },
        title: {
            text: '',
        },
        pane: {
            startAngle: -90,
            endAngle: 90,
            background: null,
            center: ['50%', '75%'],
            size: '110%',
        },
        yAxis: {
            min: 1,
            max: 5,
            tickInterval: 1,
            minorTickWidth: 0,
            minorTickLength: 0,
            tickWidth: 0,
            tickLength: 0,
            labels: {
                enabled: false,
            },
            lineWidth: 0,
            plotBands: [
                {
                    // background of gauge chart
                    from: 1,
                    to: 5,
                    color: backgroundColor || 'gray',
                    thickness: 10,
                    innerRadius: 30,
                    outerRadius: 40,
                },
                {
                    // risk indicator band
                    from: 0,
                    to: value, // value of risk
                    color: indicatorColor || '#DF5353',
                    thickness: 10,
                    innerRadius: 30,
                    outerRadius: 40,
                },
            ],
        },
        tooltip: {
            enabled: false,
        },
        series: [
            {
                type: 'gauge',
                data: [value],
                animation: false,
                tooltip: {
                    enabled: false,
                },
                dataLabels: {
                    enabled: false,
                },
                dial: {
                    radius: '50%',
                    backgroundColor: dialColor || 'gray',
                    baseWidth: 5,
                    baseLength: '0%',
                    rearLength: '0%',
                    rounded: true,
                },
                pivot: {
                    backgroundColor: dialColor || 'gray',
                    radius: 2.5,
                },
            },
        ],
    };

    // Show outer dashed line over chart
    if (showDashedOuterLine) {
        chartOptions = {
            ...chartOptions,
            yAxis: {
                ...chartOptions.yAxis,
                plotBands: [
                    {
                        from: 1,
                        to: 1.7,
                        color: dashedLineColor || '#000000',
                        thickness: 5,
                        innerRadius: 45,
                        outerRadius: 48,
                    },
                    {
                        from: 1.8,
                        to: 2.6,
                        color: dashedLineColor || '#000000',
                        thickness: 5,
                        innerRadius: 45,
                        outerRadius: 48,
                    },
                    {
                        from: 2.7,
                        to: 3.4,
                        color: dashedLineColor || '#000000',
                        thickness: 5,
                        innerRadius: 45,
                        outerRadius: 48,
                    },
                    {
                        from: 3.5,
                        to: 4.2,
                        color: dashedLineColor || '#000000',
                        thickness: 5,
                        innerRadius: 45,
                        outerRadius: 48,
                    },
                    {
                        from: 4.3,
                        to: 5,
                        color: dashedLineColor || '#000000',
                        thickness: 5,
                        innerRadius: 45,
                        outerRadius: 48,
                    },
                    ...chartOptions.yAxis.plotBands,
                ],
            },
        };
    }

    // merge options from editor to chart options
    options &&
        Array.isArray(options) &&
        options?.forEach((o) => {
            let userOptions;

            try {
                userOptions = parse(o.value || '{}');
            } catch (error) {
                userOptions = {};
            }

            chartOptions = merge(chartOptions, o.path, userOptions);
        });

    return <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />;
};
export default RiskGaugeChart;
